import { useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import {
  getBinder,
  getPolicy,
  type BoundBinder,
  type BoundPolicy,
} from "@/lib/submissions/boundCoverage";

export type BoundCoverageRead =
  | { kind: "loading" }
  | { kind: "failed" }
  | { kind: "done"; binder: BoundBinder | null; policy: BoundPolicy | null };

/**
 * A bound quote's binder and policy (PMS-S560, BRK-S468), read together and
 * again on `retry`. A record the quote names no id for is none, and with
 * neither there's nothing to read.
 */
export function useBoundCoverage(
  binderId: string | null,
  policyId: string | null,
): { read: BoundCoverageRead; retry: () => void } {
  const { authedRequest } = useAuth();
  const nothing = !binderId && !policyId;
  const [read, setRead] = useState<BoundCoverageRead>(
    nothing
      ? { kind: "done", binder: null, policy: null }
      : { kind: "loading" },
  );
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!binderId && !policyId) return;
    let cancelled = false;
    Promise.all([
      binderId ? getBinder(authedRequest, binderId) : null,
      policyId ? getPolicy(authedRequest, policyId) : null,
    ]).then(
      ([binder, policy]) => {
        if (!cancelled) setRead({ kind: "done", binder, policy });
      },
      () => {
        if (!cancelled) setRead({ kind: "failed" });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [authedRequest, binderId, policyId, attempt]);

  return {
    read,
    retry: () => {
      setRead({ kind: "loading" });
      setAttempt((n) => n + 1);
    },
  };
}
